/**
 * Who is waiting to get in, and what letting them in means.
 *
 * A Yahoo sign-in already writes `links/{uid}` with `confirmed: false` — the server does it on
 * first sign-in whether or not Yahoo will say which team the account owns. So the league has had a
 * request queue since the day sign-in shipped; what it did not have was anywhere that said so. An
 * unconfirmed link IS the request (Tim, 21 August), which is why nothing new is asked of the person
 * signing in.
 *
 * THE ROLE IS A PROPERTY OF THE PERSON, NOT OF THE ACCOUNT. Admitting somebody does two things:
 * it pairs the Yahoo account to a manager id (the server's job, `POST /api/confirm`) and it sets
 * that manager's role on the roster (the client's job, `managers/{id}`, which the rules already let
 * an admin write). Storing a role on the link instead would let the same person hold two different
 * roles depending on which account he signed in with.
 *
 * Everything here is pure so the queue can be counted, and an admission checked, without a network.
 */

import { managerShort } from './identity.js';

/** The half of `links/{uid}` this app reasons about. The server owns the rest. */

export interface AccessLink {
  uid: string;
  gm: string | null;
  confirmed: boolean;
  viewer?: boolean;
  teamName?: string | null;
  why?: string;
  /*
   * WHO THE ACCOUNT BELONGS TO. Firebase Auth has had these since the first sign-in — the sign-in
   * screen renders the name straight back at the person — and the link row simply never carried
   * them, so the admin queue said "A Yahoo account" twice and admitting anybody was a guess.
   */
  displayName?: string | null;
  email?: string | null;
  photoURL?: string | null;
  /*
   * TURNED AWAY, AND STILL ON FILE. Rejecting is not deleting: the row stays so a mis-tap can be
   * undone, so the same stranger signing in again does not reappear as a fresh request, and so
   * there is a record of who was turned away.
   */
  rejected?: boolean;
  rejectedBy?: string | null;
  /** Server timestamps, serialised. Read for the "confirmed 14 Aug" line; never a presence claim. */
  confirmedAt?: unknown;
  confirmedBy?: string | null;
  rejectedAt?: unknown;
}

/**
 * What to call an account in the queue, best available first.
 *
 * The last resort is the tail of the uid rather than "A Yahoo account": two rows reading "A Yahoo
 * account" are indistinguishable, and two reading `…f3a91c` and `…7b20de` are at least telling the
 * truth about being different people. That was the whole bug.
 */
export function accountLabel(l: AccessLink): string {
  const name = l.displayName?.trim();
  if (name) return name;
  const email = l.email?.trim();
  if (email) return email;
  return `Yahoo account ${l.uid.slice(-6)}`;
}

/** True when the row can only be identified by its uid — worth saying out loud on screen. */
export const isAnonymous = (l: AccessLink): boolean =>
  !l.displayName?.trim() && !l.email?.trim();

/**
 * What an admin may hand out. Two things, and the two that are not here are not here on purpose.
 *
 * ADMIN is the role that hands out roles; a screen that can promote a stranger to the level that
 * runs the screen is one mis-tap from locking its owner out of his own league.
 *
 * COMMISSIONER is not a property of an account (Pass 15). `firestore.rules` reads the two
 * commissioners from `league/config.commissionerGms`, and the roster carries the same; admitting
 * a man as a GM and making that GM a commissioner are two writes to two different documents, and
 * offering it here produced a roster that said "commissioner" beside a database that refused him.
 * The two commissioners are named in League configuration.
 */
export type GrantedRole = 'manager' | 'viewer';

export const ROLE_CHOICES: ReadonlyArray<{ role: GrantedRole; label: string; what: string }> = [
  {
    role: 'manager', label: 'GM',
    what: 'Holds a team. Declares keepers, ranks slots, drafts, trades, proposes rules, votes.',
  },
  { role: 'viewer', label: 'Viewer', what: 'Signed in to watch. Holds no team and writes nothing.' },
];

/** A viewer holds no seat; the other two do. 2020-II-1 and everything else keys on the seat. */
export const needsTeam = (role: GrantedRole): boolean => role !== 'viewer';

/**
 * Waiting = signed in, not yet admitted, AND NOT TURNED AWAY.
 *
 * The third clause is the one that carries weight. Without it a rejected account is still
 * "waiting": it sits in the queue it was just removed from, and it keeps the red dot on the
 * admin's avatar lit for work that has already been done.
 */
export const isWaiting = (l: AccessLink): boolean => !l.confirmed && !l.rejected;

export const waiting = (links: readonly AccessLink[]): AccessLink[] => links.filter(isWaiting);

/** Turned away. Kept, listed, and undoable — never deleted. */
export const isRejected = (l: AccessLink): boolean => l.rejected === true;

export const rejectedLinks = (links: readonly AccessLink[]): AccessLink[] =>
  links.filter(isRejected);

/**
 * The number the dot is drawn from.
 *
 * Zero is not a badge. `chrome.badges` already treats an absent key as "nothing owed" and the dot
 * renders on truthiness, so returning 0 rather than null keeps this a plain count and lets the
 * shell decide.
 */
export const waitingCount = (links: readonly AccessLink[]): number => waiting(links).length;

/* -- the lock ---------------------------------------------------------------------------------- */

/**
 * Locking closes the door to STRANGERS, not to the league (Tim, 21 August).
 *
 * An account the server has seen before keeps working — a manager who lost his session, cleared his
 * cookies or bought a phone signs straight back in. What locking stops is a brand new unrecognised
 * account creating a row for an admin to deal with. That is the difference between "everyone is in"
 * and "nobody else may ever sign in", and only the first is true once a season starts.
 *
 * `known` is whether `links/{uid}` already exists. The server is the only thing that can answer it,
 * which is why this takes it rather than looking it up.
 */
export function mayRequest(locked: boolean, known: boolean): boolean {
  return known || !locked;
}

export const LOCKED_MESSAGE =
  'The league is not taking new access requests at the moment. Ask the admin to reopen them.';

/* -- admitting somebody ------------------------------------------------------------------------ */

export interface Admission {
  uid: string;
  role: GrantedRole;
  /** The manager id this account becomes. Required for every role — a viewer is a person too. */
  gm: string;
  /** The 2026 seat. Required for a GM or a commissioner, meaningless for a viewer. */
  team?: string | null;
}

/**
 * What is wrong with this admission, in the order a person would notice it.
 *
 * Returned as sentences rather than codes because every one of them is shown verbatim: the screen
 * has room for the reason, and "invalid" on a form that decides who runs the league is not a
 * reason. An empty array means it is safe to write.
 */
export function admissionProblems(
  a: Admission,
  opts: {
    seatsTaken: Readonly<Record<string, string>>;
    /**
     * Which manager ids already have a confirmed ACCOUNT, and whose. The roster gives every seat
     * a manager, so "is this seat taken" is never the question; the question is whether another
     * Yahoo account is already sitting in it. Absent means nothing is checked at this level and
     * the server's own check is the only one.
     */
    claimedBy?: Readonly<Record<string, string>>;
  },
): string[] {
  const out: string[] = [];
  if (!a.gm) out.push(needsTeam(a.role) ? 'Choose a seat.' : 'Choose which person this account is.');
  if (needsTeam(a.role)) {
    if (!a.team) {
      if (a.gm) out.push('A GM holds a team — choose one.');
    } else {
      const holder = opts.seatsTaken[a.team];
      if (holder && holder.toUpperCase() !== a.gm.toUpperCase()) {
        out.push(`${a.team} is already ${holder}'s seat. Move him first, or pick another team.`);
      }
      const claimant = opts.claimedBy?.[a.gm.toUpperCase()];
      if (claimant && claimant !== a.uid) {
        out.push(`${a.team} is already ${managerShort(a.gm)}\u2019s seat. Move him first, or pick another team.`);
      }
    }
  }
  return out;
}

/**
 * Manager id → the uid of the confirmed account sitting in it. What `admissionProblems` and the
 * seat select both read to say which seats are free.
 */
export function claimedSeats(links: readonly AccessLink[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of links) {
    if (l.confirmed && l.gm && !l.viewer) out[l.gm.toUpperCase()] = l.uid;
  }
  return out;
}

/** A viewer's seat is dropped rather than kept — he is not holding it "for now". */
export const teamFor = (a: Admission): string | undefined =>
  (needsTeam(a.role) ? (a.team ?? undefined) : undefined);
